'use client'

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'
import styles from './DepositWithdrawModal.module.css'

/**
 * @uiComponent
 * @name DepositWithdrawModal
 * @description A modal component for managing cryptocurrency deposits and withdrawals on the Binance platform.
 * Features network selection, deposit address generation with QR codes, Lightning Network support for Bitcoin,
 * and comprehensive error handling for different blockchain networks and asset types.
 * @location Modal overlay triggered from Holdings component when clicking on an asset
 * @prop isOpen Boolean controlling the visibility of the modal
 * @prop onClose Callback function to close the modal
 * @prop asset The cryptocurrency asset symbol (e.g., BTC, ETH, BNB)
 * @prop binanceConnector The Binance connector instance for API operations
 */

/**
 * @uiWorkflow
 * @name Deposit Cryptocurrency to Binance Wallet
 * @description How to generate deposit addresses and transfer crypto to your Binance account
 * @platform binance
 * @step 1 From Holdings, click on the cryptocurrency you want to deposit [component: Holdings] [action: click]
 * @step 2 The deposit/withdraw modal will open with "Deposit" tab active [component: DepositWithdrawModal] [action: observe]
 * @step 3 Select the appropriate blockchain network from the dropdown [component: DepositWithdrawModal] [action: select] [notes: e.g., BSC, ETH, TRX]
 * @step 4 Wait for the deposit address to be generated [component: DepositWithdrawModal] [action: observe]
 * @step 5 Copy the deposit address or scan the QR code [component: DepositWithdrawModal] [action: copy]
 * @step 6 If applicable, copy the memo/tag for certain networks [component: DepositWithdrawModal] [action: copy] [notes: Required for XRP, XLM, etc.]
 * @step 7 Send cryptocurrency from your external wallet to the generated address [component: External] [action: transfer]
 * @step 8 Monitor your Holdings for the incoming deposit [component: Holdings] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Generate Bitcoin Lightning Network Invoice on Binance
 * @description How to create a Lightning Network invoice for fast Bitcoin deposits
 * @platform binance
 * @step 1 Click on Bitcoin (BTC) in your Holdings to open the modal [component: Holdings] [action: click]
 * @step 2 Ensure the "Deposit" tab is selected [component: DepositWithdrawModal] [action: click]
 * @step 3 Select "LIGHTNING" from the network dropdown [component: DepositWithdrawModal] [action: select]
 * @step 4 Enter the amount of Bitcoin you want to deposit [component: DepositWithdrawModal] [action: input]
 * @step 5 Click the generate button to create Lightning Network invoice [component: DepositWithdrawModal] [action: click]
 * @step 6 Copy the Lightning Network address or scan the QR code [component: DepositWithdrawModal] [action: copy]
 * @step 7 Pay the invoice from your Lightning Network wallet [component: External] [action: pay]
 * @step 8 The deposit will appear in your account within seconds [component: Holdings] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Withdraw Cryptocurrency from Binance Wallet
 * @description How to send cryptocurrency from your Binance account to external wallets
 * @platform binance
 * @step 1 Click on the cryptocurrency you want to withdraw from Holdings [component: Holdings] [action: click]
 * @step 2 Switch to the "Withdraw" tab in the modal [component: DepositWithdrawModal] [action: click]
 * @step 3 Select the appropriate withdrawal network [component: DepositWithdrawModal] [action: select]
 * @step 4 Enter the destination wallet address [component: DepositWithdrawModal] [action: input]
 * @step 5 Include memo/tag if required by the network [component: DepositWithdrawModal] [action: input] [notes: Check network requirements]
 * @step 6 Enter the amount you want to withdraw [component: DepositWithdrawModal] [action: input]
 * @step 7 Review withdrawal fees and minimum amounts [component: DepositWithdrawModal] [action: observe]
 * @step 8 Confirm and submit the withdrawal request [component: DepositWithdrawModal] [action: submit]
 */

/**
 * @uiWorkflow
 * @name Select Blockchain Networks for Crypto Transfers
 * @description How to choose the right network for deposits and withdrawals
 * @platform binance
 * @step 1 Open the deposit/withdraw modal for any cryptocurrency [component: DepositWithdrawModal] [action: open]
 * @step 2 Review available networks in the dropdown [component: DepositWithdrawModal] [action: observe]
 * @step 3 Choose based on your external wallet compatibility [component: DepositWithdrawModal] [action: analyze] [notes: Match sender/receiver networks]
 * @step 4 Consider transaction fees and speed differences [component: DepositWithdrawModal] [action: compare] [notes: BSC vs ETH vs TRX]
 * @step 5 Select the most appropriate network for your needs [component: DepositWithdrawModal] [action: select]
 * @step 6 Verify the network selection matches your external wallet [component: DepositWithdrawModal] [action: verify]
 * @step 7 Proceed with the deposit or withdrawal operation [component: DepositWithdrawModal] [action: continue]
 */

interface DepositWithdrawModalProps {
  isOpen: boolean
  onClose: () => void
  asset: string
  binanceConnector: any
}

interface Network {
  network: string
  name: string
  isDefault?: boolean
}

interface DepositAddress {
  address: string
  tag?: string // For networks that use memo/tags like XRP
  network: string
}

interface CoinNetworkInfo {
  network: string
  name: string
  isDefault: boolean
  depositEnable: boolean
  withdrawEnable: boolean
  depositDesc?: string
  withdrawDesc?: string
  specialTips?: string
  specialWithdrawTips?: string
  depositDust: string // Minimum deposit amount
  withdrawMin: string
  withdrawMax: string
  withdrawFee: string
  estimatedArrivalTime: number // Minutes
  minConfirm: number
  unLockConfirm: number
  busy: boolean
  contractAddress?: string
  contractAddressUrl?: string
  denomination?: number
}

interface CoinInfo {
  coin: string
  name: string
  depositAllEnable: boolean
  withdrawAllEnable: boolean
  trading: boolean
  networkList: CoinNetworkInfo[]
}

export default function DepositWithdrawModal({
  isOpen,
  onClose,
  asset,
  binanceConnector
}: DepositWithdrawModalProps) {
  const [activeTab, setActiveTab] = useState<'deposit' | 'withdraw'>('deposit')
  const [networks, setNetworks] = useState<Network[]>([])
  const [selectedNetwork, setSelectedNetwork] = useState<string>('')
  const [depositAddress, setDepositAddress] = useState<DepositAddress | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lightningAmount, setLightningAmount] = useState<string>('')
  const [mounted, setMounted] = useState(false)
  const [coinInfo, setCoinInfo] = useState<CoinInfo | null>(null)
  const [coinInfoLoading, setCoinInfoLoading] = useState(false)

  // Set mounted state to true after component mounts for portal rendering
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Fetch coin information for minimum deposit and other details
  useEffect(() => {
    if (!isOpen || !asset || !binanceConnector) return

    const fetchCoinInfo = async () => {
      setCoinInfoLoading(true)
      
      try {
        if (!binanceConnector.apiKey || !binanceConnector.apiSecret) {
          // Don't set error for missing keys, just skip fetching extra info
          setCoinInfoLoading(false)
          return
        }

        const response = await fetch('/api/connectors/binance/coinInfo', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            apiKey: binanceConnector.apiKey,
            apiSecret: binanceConnector.apiSecret,
            asset: asset
          })
        })

        if (!response.ok) {
          // Don't throw error, just log it and continue without extra info
          console.log('Could not fetch coin info:', response.statusText)
          setCoinInfoLoading(false)
          return
        }

        const coinData = await response.json()
        setCoinInfo(coinData)
      } catch (err) {
        console.log('Error fetching coin info:', err)
        // Don't show error to user, just continue without extra info
      } finally {
        setCoinInfoLoading(false)
      }
    }

    fetchCoinInfo()
  }, [isOpen, asset, binanceConnector])

  // Helper function to get current network info
  const getCurrentNetworkInfo = () => {
    if (!coinInfo || !selectedNetwork) return null
    return coinInfo.networkList.find(n => n.network === selectedNetwork)
  }

  // Helper function to format deposit amount with proper decimals
  const formatDepositAmount = (amount: string) => {
    const num = parseFloat(amount)
    if (isNaN(num)) return amount
    
    // Format based on the amount size
    if (num < 0.0001) {
      return num.toFixed(8)
    } else if (num < 1) {
      return num.toFixed(6)
    } else if (num < 1000) {
      return num.toFixed(4)
    } else {
      return num.toLocaleString()
    }
  }

  // Helper function to replace "Binance" with "NINX" for branding consistency
  const formatBrandingText = (text: string) => {
    return text.replace(/Binance/gi, 'NINX')
  }

  const handleLightningSubmit = () => {
    // Validate the amount
    const amount = parseFloat(lightningAmount)
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0')
      return
    }

    // Fetch deposit address with the validated amount
    fetchDepositAddress(lightningAmount)
  }

  // Fetch available networks for the asset
  useEffect(() => {
    if (!isOpen || !asset) return

    const fetchNetworks = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // This would be the actual API call to get networks
        // For now, we'll simulate with some common networks
        const response = await fetch(
          `/api/connectors/binance/networks?asset=${asset}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              apiKey: binanceConnector.apiKey,
              apiSecret: binanceConnector.apiSecret,
              asset: asset
            })
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch networks')
        }

        const networksData = await response.json()
        setNetworks(networksData)

        // Select the default network
        const defaultNetwork =
          networksData.find((n: Network) => n.isDefault)?.network ||
          networksData[0]?.network
        if (defaultNetwork) {
          setSelectedNetwork(defaultNetwork)
        }
      } catch (err: any) {
        console.error('Error fetching networks:', err)
        setError(err.message || 'Failed to fetch networks')

        // Fallback to some common networks for testing
        const fallbackNetworks = [
          { network: 'BSC', name: 'BNB Smart Chain (BEP20)', isDefault: true },
          { network: 'ETH', name: 'Ethereum (ERC20)' }
        ]

        setNetworks(fallbackNetworks)
        setSelectedNetwork(fallbackNetworks[0].network)
      } finally {
        setIsLoading(false)
      }
    }

    fetchNetworks()
  }, [isOpen, asset, binanceConnector])

  // Function to fetch deposit address
  const fetchDepositAddress = async (amount?: string) => {
    if (!selectedNetwork || !asset) return

    setIsLoading(true)
    setError(null)

    try {
      const requestBody: any = {
        apiKey: binanceConnector.apiKey,
        apiSecret: binanceConnector.apiSecret,
        asset: asset,
        network: selectedNetwork
      }

      // Add amount for Lightning Network invoices
      if (asset === 'BTC' && selectedNetwork === 'LIGHTNING' && amount) {
        const parsedAmount = parseFloat(amount)
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
          throw new Error('Please enter a valid amount greater than 0')
        }
        requestBody.amount = parsedAmount
      }

      const response = await fetch(`/api/connectors/binance/depositAddress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        throw new Error('Failed to fetch deposit address')
      }

      const addressData = await response.json()

      // Special handling for BTC Lightning Network
      if (asset === 'BTC' && selectedNetwork === 'LIGHTNING') {
        // Ensure we have a valid Lightning Network address
        if (!addressData.address || !addressData.address.startsWith('ln')) {
          throw new Error('Invalid Lightning Network address format')
        }
      }

      setDepositAddress(addressData)
    } catch (err: any) {
      console.error('Error fetching deposit address:', err)
      setError(err.message || 'Failed to fetch deposit address')

      // Fallback for testing
      setDepositAddress({
        address: '0x',
        network: selectedNetwork
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch deposit address when network is selected (except for Lightning Network)
  useEffect(() => {
    if (!selectedNetwork || !asset) return

    // Skip fetching for Lightning Network as it requires an amount
    if (asset === 'BTC' && selectedNetwork === 'LIGHTNING') {
      setDepositAddress(null)
      setError('Please enter an amount to generate a Lightning Network invoice')
      setIsLoading(false)
      return
    }

    fetchDepositAddress()
  }, [selectedNetwork, asset])

  if (!isOpen || !mounted) return null

  return createPortal(
    <div 
      className={styles.modalOverlay}
      onClick={onClose}
    >
      <div 
        className={`${styles.modalContent} ${styles.blurredDialog}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 className="text-xl font-bold">
            {asset} {activeTab === 'deposit' ? 'Deposit' : 'Withdraw'}
          </h2>
          <button 
            className={styles.closeButton}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabContainer}>
          <button
            className={`${styles.tab} ${activeTab === 'deposit' ? styles.activeTab : styles.inactiveTab}`}
            onClick={() => setActiveTab('deposit')}
          >
            Deposit
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'withdraw' ? styles.activeTab : styles.inactiveTab}`}
            onClick={() => setActiveTab('withdraw')}
          >
            Withdraw
          </button>
        </div>

        {activeTab === 'deposit' ? (
          <div className="space-y-4">
            {/* Network Selection */}
            <div>
              <label className="block text-sm font-medium mb-1">Network</label>
              {isLoading && networks.length === 0 ? (
                <div
                  className={`${styles.shimmerSelect} ${styles.shimmer}`}
                ></div>
              ) : (
                <select
                  className="w-full p-2 border rounded bg-background text-black"
                  value={selectedNetwork}
                  onChange={e => setSelectedNetwork(e.target.value)}
                  disabled={isLoading}
                >
                  {networks.length === 0 ? (
                    <option>No networks available</option>
                  ) : (
                    networks.map(network => (
                      <option key={network.network} value={network.network}>
                        {network.name}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>

            {/* Lightning Network Amount Input - Only show for BTC Lightning */}
            {asset === 'BTC' && selectedNetwork === 'LIGHTNING' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Amount (BTC)
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    className="flex-1 p-2 border rounded bg-background text-black"
                    placeholder="Enter amount in BTC"
                    value={lightningAmount}
                    onChange={e => setLightningAmount(e.target.value)}
                    step="0.00000001"
                    min="0.00000001"
                    disabled={isLoading}
                    required
                  />
                  <button
                    className="px-4 py-2 bg-[#21728d] text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleLightningSubmit}
                    disabled={
                      isLoading ||
                      !lightningAmount ||
                      parseFloat(lightningAmount) <= 0
                    }
                  >
                    Generate Invoice
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Lightning Network invoices require a specific amount. (max 0.01)
                </p>
              </div>
            )}

            {/* Deposit Address */}
            <div className="space-y-4 min-h-[400px]">
              {isLoading ? (
                <div className={styles.shimmerContainer}>
                  {/* Shimmer for QR code */}
                  <div className="flex justify-center">
                    <div
                      className={`${styles.shimmerQR} ${styles.shimmer}`}
                    ></div>
                  </div>

                  {/* Shimmer for address input */}
                  <div>
                    <div
                      className={`${styles.shimmerText} ${styles.shimmer} mb-1`}
                    ></div>
                    <div className="flex">
                      <div
                        className={`${styles.shimmerInput} ${styles.shimmer} w-full`}
                      ></div>
                      <div
                        className={`${styles.shimmerButton} ${styles.shimmer} ml-2`}
                      ></div>
                    </div>
                  </div>

                  {/* Shimmer for warning box */}
                  <div className={`${styles.shimmer} p-3 rounded h-24`}></div>
                </div>
              ) : error ? (
                <div className="text-red-500 py-4">{error}</div>
              ) : depositAddress ? (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    {asset === 'BTC' && selectedNetwork === 'LIGHTNING' ? (
                      <QRCodeSVG
                        value={depositAddress.address}
                        size={200}
                        level="H"
                        includeMargin={true}
                        // Lightning addresses can be long, so we use a smaller QR code with higher error correction
                        bgColor={'#ffffff'}
                        fgColor={'#000000'}
                      />
                    ) : (
                      <QRCodeSVG
                        value={depositAddress.address}
                        size={200}
                        level="H"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Address
                    </label>
                    <div className="flex">
                      <input
                        type="text"
                        readOnly
                        value={depositAddress.address}
                        className="w-full p-2 border rounded bg-background text-black"
                      />
                      <button
                        className={styles.copyButton}
                        onClick={() =>
                          navigator.clipboard.writeText(depositAddress.address)
                        }
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  {depositAddress.tag && (
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Memo/Tag
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          readOnly
                          value={depositAddress.tag}
                          className="w-full p-2 border rounded bg-background text-black"
                        />
                        <button
                          className={styles.copyButton}
                          onClick={() =>
                            navigator.clipboard.writeText(
                              depositAddress.tag || ''
                            )
                          }
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="text-sm text-yellow-500 p-3 bg-yellow-50 rounded">
                    <p className="font-medium">Important:</p>
                    <ul className="list-disc pl-5 mt-1">
                      <li>Send only {asset} to this deposit address.</li>
                      <li>
                        Ensure you're using the{' '}
                        {
                          networks.find(n => n.network === selectedNetwork)
                            ?.name
                        }{' '}
                        network.
                      </li>
                      
                      {/* Show minimum deposit amount from coin info */}
                      {(() => {
                        const networkInfo = getCurrentNetworkInfo()
                        if (networkInfo?.depositDust) {
                          return (
                            <li>
                              <strong>Minimum deposit:</strong> {formatDepositAmount(networkInfo.depositDust)} {asset}
                            </li>
                          )
                        }
                        return null
                      })()}

                      {/* Show estimated arrival time */}
                      {(() => {
                        const networkInfo = getCurrentNetworkInfo()
                        if (networkInfo?.estimatedArrivalTime) {
                          const time = networkInfo.estimatedArrivalTime
                          const timeText = time === 1 ? '1 minute' : `${time} minutes`
                          return (
                            <li>
                              <strong>Estimated arrival time:</strong> {timeText}
                            </li>
                          )
                        }
                        return null
                      })()}

                      {/* Show confirmations required */}
                      {(() => {
                        const networkInfo = getCurrentNetworkInfo()
                        if (networkInfo?.minConfirm) {
                          return (
                            <li>
                              <strong>Required confirmations:</strong> {networkInfo.minConfirm}
                            </li>
                          )
                        }
                        return null
                      })()}

                      {/* Show special tips from Binance */}
                      {(() => {
                        const networkInfo = getCurrentNetworkInfo()
                        if (networkInfo?.specialTips) {
                          const formattedTips = formatBrandingText(networkInfo.specialTips)
                          return (
                            <li>
                              <strong>Note:</strong> {formattedTips}
                            </li>
                          )
                        }
                        return null
                      })()}

                      {/* Show if network is busy */}
                      {(() => {
                        const networkInfo = getCurrentNetworkInfo()
                        if (networkInfo?.busy) {
                          return (
                            <li className="text-red-500">
                              <strong>Network Status:</strong> Network is currently busy. Deposits may take longer than usual.
                            </li>
                          )
                        }
                        return null
                      })()}

                      {/* Show deposit status */}
                      {(() => {
                        const networkInfo = getCurrentNetworkInfo()
                        if (networkInfo && !networkInfo.depositEnable && networkInfo.depositDesc) {
                          const formattedDesc = formatBrandingText(networkInfo.depositDesc)
                          return (
                            <li className="text-red-500">
                              <strong>Deposit Status:</strong> {formattedDesc}
                            </li>
                          )
                        }
                        return null
                      })()}

                      {depositAddress.tag && (
                        <li>
                          Both the address and memo/tag are required for
                          successful deposit.
                        </li>
                      )}
                      {asset === 'BTC' &&
                        selectedNetwork === 'LIGHTNING' && (
                          <>
                            <li>
                              This is a Lightning Network invoice. It can only
                              be paid using a Lightning-compatible wallet.
                            </li>
                            <li>
                              Lightning Network deposits are typically processed
                              instantly.
                            </li>
                          </>
                        )}
                    </ul>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="min-h-[400px]">
            {isLoading ? (
              <div className={styles.shimmerContainer}>
                <div
                  className={`${styles.shimmerText} ${styles.shimmer} mb-3`}
                ></div>
                <div
                  className={`${styles.shimmerInput} ${styles.shimmer} mb-3`}
                ></div>
                <div
                  className={`${styles.shimmerText} ${styles.shimmer} mb-3`}
                ></div>
                <div
                  className={`${styles.shimmerInput} ${styles.shimmer} mb-3`}
                ></div>
                <div className="flex justify-end">
                  <div
                    className={`${styles.shimmerButton} ${styles.shimmer}`}
                  ></div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <p>
                  Withdraw functionality will be implemented in a future update
                  if too many users request it. For now, please withdraw using the app(more secure).
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
